// Utility to create a toast-compatible API for migration from react-toastify
import { useToast as useToastContext } from '../context/toastContext';

/**
 * Hook that provides a toast API compatible with react-toastify for easier migration
 * Can be used as a drop-in replacement: const toast = useToast();
 */
export const useToast = () => {
  const toastContext = useToastContext();

  // Return an object with methods that match react-toastify API
  return {
    success: toastContext.success,
    error: toastContext.error,
    warning: toastContext.warning,
    warn: toastContext.warning, // Alias for compatibility
    info: toastContext.info,
  };
};

// Export a standalone toast object for non-hook usage (services, etc.)
// Note: This requires the ToastProvider to be mounted
let toastRef: ReturnType<typeof useToastContext> | null = null;

export const setToastRef = (ref: ReturnType<typeof useToastContext>) => {
  toastRef = ref;
};

export const toast = {
  success: (message: string, duration?: number, action?: { label: string; onClick: () => void }) => {
    if (toastRef) toastRef.showToast(message, 'success', duration ?? 3000, action);
  },
  error: (message: string, duration?: number, action?: { label: string; onClick: () => void }) => {
    if (toastRef) toastRef.showToast(message, 'error', duration ?? 3000, action);
  },
  warning: (message: string, duration?: number, action?: { label: string; onClick: () => void }) => {
    if (toastRef) toastRef.showToast(message, 'warning', duration ?? 3000, action);
  },
  warn: (message: string, duration?: number, action?: { label: string; onClick: () => void }) => {
    if (toastRef) toastRef.showToast(message, 'warning', duration ?? 3000, action);
  },
  info: (message: string, duration?: number, action?: { label: string; onClick: () => void }) => {
    if (toastRef) toastRef.showToast(message, 'info', duration ?? 0, action);
  },
};
